import { ChangeEvent, ReactNode, useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import styles from '@components/fields/ToggleCheckbox.module.css';
import BodyCopy from '@components/typography/BodyCopy';
import { maybeWrapElementInHoverText } from '@utils/htmlUtils';
import { clickCurrentTarget } from '@utils/keyboardUtils';

type Props = {
  checked?: boolean;
  disabled?: boolean;
  hoverContent?: ReactNode;
  labelKey?: string;
  name: string;
  onChange?: (checked: boolean, event?: ChangeEvent<HTMLInputElement>) => void;
  onClick?: () => void;
  skipSpacing?: boolean;
  tabIndex: number;
};

const ToggleCheckbox = ({
  checked = false,
  disabled = false,
  hoverContent,
  labelKey,
  name,
  onChange,
  onClick,
  skipSpacing = false
}: Props) => {
  const handleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (onChange && checked !== event.target.checked) {
        onChange(event.target.checked, event);
      }
    },
    [checked, onChange]
  );
  const [inputFocused, setInputFocused] = useState(false);

  return maybeWrapElementInHoverText(
    hoverContent,
    <div className={labelKey && !skipSpacing ? styles.labelSpacing : ''}>
      <label className={`${styles.container} ${disabled ? styles.disabled : ''}`} data-test="toggle-checkbox">
        <input
          checked={checked}
          className={styles.checkboxSquare}
          disabled={disabled}
          name={name}
          onBlur={() => setInputFocused(false)}
          onChange={handleChange}
          onClick={onClick}
          onKeyUp={clickCurrentTarget}
          tabIndex={disabled ? -1 : 0}
          type="checkbox"
        />
        <span
          className={`${styles.checkmark} ${inputFocused ? styles.focus : ''}`}
          data-test="interactive-checkbox"
        ></span>
        {labelKey && (
          <div className={skipSpacing ? styles.centerSpacing : styles.labelSpacing}>
            <BodyCopy color="primary700">
              <FormattedMessage id={labelKey} />
            </BodyCopy>
          </div>
        )}
      </label>
    </div>
  );
};

export default ToggleCheckbox;
